import React, { useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Plus,
  Search,
  User,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetAllContractsQuery } from '@/apis/extended/agentApi';
import { useConfirmNegotiationsMutation } from '@/apis/extended/contractApi';
import { useGenerateRatifiedContractMutation } from '@/apis/extended/contractRatificationApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { parseDateOnly } from '@/utils/dateOnly';

const AgentDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { data: contracts } = useGetAllContractsQuery(agentId ?? '');
  const [confirmNegotiations, { isLoading: isConfirming }] =
    useConfirmNegotiationsMutation();
  const [generateContract, { isLoading: isGenerating }] =
    useGenerateRatifiedContractMutation();

  const handleConfirmAndGenerate = async (contractId: string) => {
    try {
      // Step 1: Confirm negotiations are complete
      await confirmNegotiations({ contractId }).unwrap();
      toast.success('Negotiations confirmed!');

      // Step 2: Generate the ratified contract
      await generateContract({ contractId }).unwrap();
      toast.success('Contract generated and ready for signatures!');

      // Refresh or navigate to contract view
      navigate(`/view-contract/${contractId}`);
    } catch (error: any) {
      console.error('Error confirming negotiations:', error);
      toast.error(error?.data?.error || 'Failed to confirm negotiations');
    }
  };

  const getStatusBadge = (workflowState: string) => {
    switch (workflowState) {
      case 'draft':
        return (
          <Badge
            variant="secondary"
            className="bg-[var(--surface-container-high)] text-[var(--on-surface-variant)]"
          >
            Draft
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-[var(--primary-navy)] text-white">
            Submitted
          </Badge>
        );
      case 'lender-review':
        return (
          <Badge className="bg-[var(--primary-container)] text-white">
            Lender Review
          </Badge>
        );
      case 'lender-approved':
        return (
          <Badge variant="trust" className="text-[var(--tertiary-fixed)]">
            Lender Approved
          </Badge>
        );
      case 'agent-review':
        return (
          <Badge className="bg-[var(--primary-navy)] text-white">
            Agent Review
          </Badge>
        );
      case 'agent-responded':
        return (
          <Badge className="bg-[var(--primary-container)] text-white">
            Agent Responded
          </Badge>
        );
      case 'seller-review':
        return (
          <Badge className="bg-[var(--primary-container)] text-white">
            Seller Review
          </Badge>
        );
      case 'seller-responded':
        return (
          <Badge className="bg-[var(--primary-container)] text-white">
            Seller Responded
          </Badge>
        );
      case 'buyer-review':
        return (
          <Badge className="bg-[var(--primary-navy)] text-white">
            Your Review Needed
          </Badge>
        );
      case 'accepted':
        return (
          <Badge variant="trust" className="text-[var(--tertiary-fixed)]">
            Accepted
          </Badge>
        );
      case 'rejected':
        return <Badge className="bg-[#f50057] text-white">Rejected</Badge>;
      case 'expired':
        return (
          <Badge variant="outline" className="text-[var(--on-surface-variant)]">
            Expired
          </Badge>
        );
      case 'ratified':
        return (
          <Badge className="bg-green-600 text-white border-green-600 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Ratified
          </Badge>
        );
      case 'negotiating':
      case 'in_negotiation':
        return (
          <Badge className="bg-yellow-600 text-white">In Negotiation</Badge>
        );
      case 'awaiting_signatures':
        return (
          <Badge className="bg-orange-600 text-white">
            Awaiting Signatures
          </Badge>
        );
      case 'awaiting_seller_signature':
        return (
          <Badge className="bg-orange-500 text-white">
            Awaiting Seller Signature
          </Badge>
        );
      case 'awaiting_buyer_signature':
        return (
          <Badge className="bg-orange-500 text-white">
            Awaiting Buyer Signature
          </Badge>
        );
      case 'negotiations_complete':
        return (
          <Badge className="bg-blue-600 text-white">
            Negotiations Complete
          </Badge>
        );
      default:
        return null;
    }
  };

  // const getStatusIcon = (workflowState: string) => {
  //   switch (workflowState) {
  //     case 'draft':
  //       return <Clock className="h-5 w-5 text-gray-400" />;
  //     case 'submitted':
  //     case 'lender-review':
  //     case 'agent-review':
  //     case 'seller-review':
  //       return <Clock className="h-5 w-5 text-yellow-500" />;
  //     case 'lender-approved':
  //     case 'accepted':
  //       return <CheckCircle className="h-5 w-5 text-green-500" />;
  //     case 'agent-responded':
  //     case 'seller-responded':
  //     case 'buyer-review':
  //       return <AlertCircle className="h-5 w-5 text-blue-500" />;
  //     case 'rejected':
  //       return <XCircle className="h-5 w-5 text-red-500" />;
  //     case 'expired':
  //       return <Clock className="h-5 w-5 text-gray-400" />;
  //     default:
  //       return null;
  //   }
  // };

  // const renderOfferCard = (offer: any) => (
  //   <Card key={offer.id} className="mb-4">
  //     <CardContent className="p-6">
  //       <div className="flex justify-between items-start mb-4">
  //         <div>
  //           <h3 className="font-medium text-lg">{offer.property.address}</h3>
  //           <p className="text-gray-500">
  //             {offer.property.city}, {offer.property.state} {offer.property.zip}
  //           </p>
  //         </div>
  //         <div className="flex items-center">
  //           {getStatusBadge(offer.workflowState)}
  //         </div>
  //       </div>

  //       <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
  //         <div>
  //           <p className="text-sm text-gray-500">Offer Amount</p>
  //           <p className="font-medium">
  //             ${offer.purchasePrice.toLocaleString()}
  //           </p>
  //         </div>
  //         <div>
  //           <p className="text-sm text-gray-500">Offer Date</p>
  //           <p className="font-medium">
  //             {offer.offerDate.toLocaleDateString()}
  //           </p>
  //         </div>
  //         <div>
  //           <p className="text-sm text-gray-500">Closing Date</p>
  //           <p className="font-medium">
  //             {offer.closingDate.toLocaleDateString()}
  //           </p>
  //         </div>
  //         <div>
  //           <p className="text-sm text-gray-500">Financing</p>
  //           <p className="font-medium capitalize">{offer.financingType}</p>
  //         </div>
  //       </div>

  //       {offer.workflowState === 'buyer-review' && (
  //         <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-md mb-4 flex items-center">
  //           <AlertCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
  //           <p className="text-sm text-blue-800 dark:text-blue-300">
  //             Action required: Review updates from{' '}
  //             {offer.currentReviewer === 'seller-agent'
  //               ? "the seller's agent"
  //               : 'the seller'}
  //           </p>
  //         </div>
  //       )}

  //       <div className="flex justify-end">
  //         <Button asChild>
  //           <Link to={`/transactions/${offer.id}`}>
  //             View Details <ArrowRight className="ml-2 h-4 w-4" />
  //           </Link>
  //         </Button>
  //       </div>
  //     </CardContent>
  //   </Card>
  // );
  return (
    <div className="min-h-screen bg-[var(--surface)] text-[var(--on-surface)]">
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-[var(--on-surface)] mb-2">
              Agent Dashboard
            </h1>
            <p className="text-[var(--on-surface-variant)]">
              Manage and track your real estate contracts efficiently
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <FileText className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Active Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                {contracts?.length || 0}
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Offers in progress
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <User className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Total Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                {contracts?.length || 0}
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Total Contracts in progress
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-low)] transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center text-[var(--on-surface)]">
                <Calendar className="h-5 w-5 mr-2 text-[var(--primary-navy)]" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[var(--on-surface)] mb-1">
                0
              </div>
              <p className="text-sm text-[var(--on-surface-variant)]">
                Deadlines this week
              </p>
            </CardContent>
          </Card>
        </div>
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--on-surface-variant)]" />
            <Input
              placeholder="Search contracts by address or ID..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <Tabs defaultValue="active">
          <TabsList className="mb-6 bg-[var(--surface-container-low)]">
            <TabsTrigger
              value="active"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Completed
            </TabsTrigger>
            <TabsTrigger
              value="drafts"
              className="data-[state=active]:bg-[var(--primary-navy)] data-[state=active]:text-white text-[var(--on-surface-variant)]"
            >
              Drafts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {contracts?.map((contract: any) => {
              // Get property address from product (source of truth) or legacy agentSellerInfo
              const propertyAddress =
                contract?.sellerPropertyInfo?.propertyAddress ||
                contract?.product?.address?.fullAddress ||
                [
                  contract?.product?.address?.street,
                  contract?.product?.address?.city,
                  contract?.product?.address?.state,
                  contract?.product?.address?.zip,
                ]
                  .filter(Boolean)
                  .join(', ') ||
                contract?.product?.address?.street ||
                contract?.agentSellerInfo?.propertyAddress ||
                contract?.propertyInfo?.address?.street ||
                'Property Address';

              const isRatified = contract?.contractStatus === 'ratified';

              return (
                <Card
                  key={contract._id}
                  className={`mb-4 border transition-colors ${
                    isRatified
                      ? 'border-emerald-300 bg-emerald-50/90 hover:bg-emerald-100'
                      : 'border-[var(--outline-variant)] bg-[var(--surface-container-lowest)] hover:bg-[var(--surface-container-high)]'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-medium text-lg text-[var(--on-surface)] flex items-center gap-2">
                          {propertyAddress}
                          {isRatified && (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          )}
                        </h3>
                      </div>
                      <div className="flex items-center">
                        {getStatusBadge(contract?.contractStatus || 'draft')}
                      </div>
                    </div>

                    {isRatified && (
                      <div className="mb-4 flex items-center rounded-md border border-emerald-300 bg-emerald-100/70 p-3">
                        <CheckCircle className="mr-2 h-5 w-5 flex-shrink-0 text-emerald-600" />
                        <div>
                          <p className="text-sm font-medium text-emerald-900">
                            Contract Fully Ratified
                          </p>
                          <p className="text-xs text-emerald-800">
                            Both parties have signed. Contract is complete.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 justify-between items-center mb-4">
                      <div>
                        <p className="text-sm text-[var(--on-surface-variant)]">
                          Offer Amount
                        </p>
                        <p className="font-medium text-[var(--on-surface)]">
                          $
                          {contract?.offerSpecifics?.offerPrice?.toLocaleString() ||
                            '0'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[var(--on-surface-variant)]">
                          Offer Date
                        </p>
                        <p className="font-medium text-[var(--on-surface)]">
                          {contract.createdAt
                            ? new Date(contract.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[var(--on-surface-variant)]">
                          Closing Date
                        </p>
                        <p className="font-medium text-[var(--on-surface)]">
                          {contract?.offerSpecifics?.closingDate
                            ? parseDateOnly(
                                contract.offerSpecifics.closingDate
                              )?.toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[var(--on-surface-variant)]">
                          Financing Commitment
                        </p>
                        <p className="font-medium capitalize text-[var(--on-surface)]">
                          {contract?.offerSpecifics?.financingCommittmentDate
                            ? parseDateOnly(
                                contract.offerSpecifics.financingCommittmentDate
                              )?.toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[var(--on-surface-variant)]">
                          Financing Type
                        </p>
                        <p className="font-medium capitalize text-[var(--on-surface)]">
                          {contract?.offerSpecifics?.offerType || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* <div className="bg-[#043a93]/20 border border-[#043a93]/30 p-3 rounded-md mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 text-[var(--primary-navy)] mr-2 flex-shrink-0" />
                    <p className="text-sm text-blue-200">
                      Action required: Review updates from the seller's agent
                    </p>
                  </div> */}

                    <div className="flex justify-end gap-2 flex-wrap">
                      <Button
                        className={`${
                          isRatified
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-[var(--primary-navy)] hover:opacity-90'
                        } text-white`}
                        onClick={() =>
                          navigate(`/agent/property-dashboard/${contract._id}`)
                        }
                      >
                        Property Dashboard{' '}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      {!isRatified && (
                        <Button
                          variant="outline"
                          className="border-[var(--primary-container)] text-[var(--on-surface)] hover:bg-[var(--surface-container-low)]"
                          onClick={() =>
                            navigate(`/agent/contract/${contract._id}`)
                          }
                        >
                          Negotiate <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}

                      {/* Show confirmation button when negotiations can be finalized */}
                      {(contract?.contractStatus === 'in_negotiation' ||
                        contract?.contractStatus ===
                          'negotiations_complete') && (
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            const contractId = contract._id?.toString();
                            if (contractId) {
                              handleConfirmAndGenerate(contractId);
                            } else {
                              toast.error('Contract ID not found');
                              console.error('Contract structure:', contract);
                            }
                          }}
                          disabled={isConfirming || isGenerating}
                        >
                          {isConfirming || isGenerating
                            ? 'Processing...'
                            : 'Confirm & Generate Contract'}
                          <CheckCircle className="ml-2 h-4 w-4" />
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        className="text-[var(--on-surface)] hover:bg-[var(--surface-container-low)]"
                        onClick={() =>
                          navigate(`/view-contract/${contract._id}`)
                        }
                      >
                        View Contract <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="completed">
            <div className="text-center py-12">
              <div className="rounded-lg bg-[var(--surface-container-low)] p-8">
                <CheckCircle className="mx-auto mb-4 h-12 w-12 text-[var(--on-tertiary-fixed-variant)]" />
                <h3 className="mb-2 text-lg font-medium text-[var(--on-surface)]">
                  No completed contracts
                </h3>
                <p className="text-[var(--on-surface-variant)]">
                  Completed contracts will appear here once finalized.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="drafts">
            <div className="text-center py-12">
              <div className="rounded-lg bg-[var(--surface-container-low)] p-8">
                <FileText className="mx-auto mb-4 h-12 w-12 text-[var(--on-surface-variant)]" />
                <h3 className="mb-2 text-lg font-medium text-[var(--on-surface)]">
                  No draft contracts
                </h3>
                <p className="text-[var(--on-surface-variant)]">
                  Start creating your first contract draft.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AgentDashboard;
